import React, { useState, useEffect } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import LogoImg from "../../assets/images/Logo.png";
import { Form, Input, Select, Button } from "antd";
import Swal from "sweetalert2";
import { config } from "../../config/config";
import axios from "axios";
import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
import ValidationHistoryTable from "./History/ValidationHistory.jsx";

function Validation() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [banks, setBanks] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(false);
  const [validationPrice, setValidationPrice] = useState(1000);
  const [selectedValidationType, setSelectedValidationType] = useState("No Record Found");
  const [priceLoading, setPriceLoading] = useState(false);
  const [validationPrices, setValidationPrices] = useState({
    "No Record Found": 1000,
    "Modification Validation": 2500,
  });
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  // Get userId from encrypted localStorage
  let userId = null;
  let userPhone = "";
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      userId = userObj?._id || userObj?.id;
      userPhone = userObj?.phone || userObj?.phoneNumber;
    }
  } catch (error) {
  }

  useEffect(() => {
    if (userPhone) {
      form.setFieldsValue({ phoneNumber: userPhone });
    }
  }, [userPhone, form, userId]);

  // Fetch validation prices from API
  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );

        const validationPricing = response.data.find(
          (item) => item.key === "validation"
        );

        // Find modification pricing
        const modificationPricing = response.data.find(
          (item) => item.key === "modification" || item.key === "modificationValidation" || item.key === "validationModification"
        );

        
        

        setValidationPrices((prev) => {
          const newPrices = { ...prev };
          if (validationPricing?.prices?.agent) {
            newPrices["No Record Found"] = validationPricing.prices.agent;
            setValidationPrice(validationPricing.prices.agent);
          }
          if (modificationPricing?.prices?.agent) {
            newPrices["Modification Validation"] = modificationPricing.prices.agent;
          } else if (validationPricing?.prices?.modification) {
            newPrices["Modification Validation"] = validationPricing.prices.modification;
          }
          
          
          return newPrices;
        });
      } catch (error) {
        toast.error("Failed to fetch current prices");
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  const onFinish = async (values) => {
    const price = validationPrices[values.validationType] || 0;

    // Show confirmation dialog first
    const result = await Swal.fire({
      title: "Confirm Search",
      text: `Are you sure you want to proceed with this Validation Service? Amount: ₦${price}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        validationType: "No Record Found",
        nin: values.nin,
        amount: price,
        userId: userId,
        phone: userPhone,
      };
      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.Validation}`,
        payload
      );
      

      await Swal.fire({
        icon: "success",
        title: "Registration Successful!",
        text: " ",
        confirmButtonColor: "#f59e0b",
      });

      form.resetFields();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text:
          error.response?.data?.error ||
          error.message ||
          "Failed to submit request. Please try again.",
        confirmButtonColor: "#f59e0b",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidationTypeChange = (value) => {
    setSelectedValidationType(value);
    const price = validationPrices[value] || 0;
    setValidationPrice(price);
  };

  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Validation Services</h2>
            <p className="text-sm text-gray-500 mt-1">Validate your information</p>
          </div>
          <div className="bg-sky-50 px-4 py-2 rounded-lg border border-sky-100 text-sky-700 font-bold text-lg shadow-sm">
            {priceLoading ? "..." : `₦${validationPrice.toLocaleString()}`}
          </div>
        </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark={true}
        initialValues={{ validationType: "No Record Found" }}
      >
        {/* Slip Selection */}
        <Form.Item
          name="validationType"
          label="Select Slip"
          rules={[{ required: true, message: "Please select slip type" }]}
        >
          <Select
            size="large"
            placeholder="Select Validation type"
            onChange={handleValidationTypeChange}
          >
            <Select.Option value="No Record Found">
              No Record Found (₦{validationPrices["No Record Found"]?.toLocaleString() || 1000})
            </Select.Option>

            <Select.Option value="Modification Validation">
              Modification Validation (₦{validationPrices["Modification Validation"]?.toLocaleString() || 2500})
            </Select.Option>
          </Select>
        </Form.Item>

        <div className="mt-4">

          <div className="flex items-stretch gap-3">
            <Form.Item
              name="nin"
              rules={[
                { required: true, message: "Please enter your NIN" },
                { pattern: /^\d{11}$/, message: "NIN must be exactly 11 digits" },
              ]}
              className="mb-0 flex-1 min-w-0"
            >
              <Input
                size="large"
                placeholder="Enter NIN/Tracking id and Send"
                autoComplete="off"
                className="rounded-lg"
              />
            </Form.Item>
            <button
              type="submit"
              disabled={loading}
              className={`flex-shrink-0 px-6 rounded-lg font-semibold text-white cursor-pointer transition-colors self-start h-[40px] ${
                loading ? "bg-gray-400" : "bg-[#2eca7f] hover:bg-[#25b571]"
              }`}
            >
              {loading ? (
                <AiOutlineLoading3Quarters className="animate-spin text-xl" />
              ) : (
                "Process"
              )}
            </button>
          </div>
        </div>
      </Form>
      </div>

      {/* History Table */}
      <div className="w-full mt-10">
        <ValidationHistoryTable />
      </div>

    </div>
  );
}

export default Validation;
